import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useUpdateNote } from '../../hooks/useAccountingNotes';
import { AccountingNote } from '../../types/accounting-notes';

interface NoteEditModalProps {
  open: boolean;
  onClose: () => void;
  note: AccountingNote;
  onSuccess?: () => void;
}

/**
 * NoteEditModal - Modal for editing an existing note
 */
export const NoteEditModal: React.FC<NoteEditModalProps> = ({
  open,
  onClose,
  note,
  onSuccess,
}) => {
  const [content, setContent] = useState(note.content);
  const [error, setError] = useState<string | null>(null);

  const updateNoteMutation = useUpdateNote();

  // Update content when note changes
  useEffect(() => {
    setContent(note.content);
  }, [note.content]);

  const handleSubmit = async () => {
    // Validate content
    const trimmedContent = content.trim();
    if (!trimmedContent) {
      setError('Note content is required');
      return;
    }

    // Check if content has changed
    if (trimmedContent === note.content) {
      onClose();
      return;
    }

    setError(null);

    try {
      await updateNoteMutation.mutateAsync({
        noteId: note.id,
        content: trimmedContent,
        entityType: note.entityType,
        entityId: note.entityId,
      });

      // Success - close modal
      onSuccess?.();
      onClose();
    } catch (err: any) {
      console.error('Error updating note:', err);
      setError(err.message || 'Failed to update note');
    }
  };

  const handleClose = () => {
    if (!updateNoteMutation.isPending) {
      setContent(note.content);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Edit Note</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            label="Note"
            fullWidth
            multiline
            rows={4}
            required
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={updateNoteMutation.isPending}
            autoFocus
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={updateNoteMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={updateNoteMutation.isPending || !content.trim()}
        >
          {updateNoteMutation.isPending ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NoteEditModal;
